import { useMemo } from "react";
import { useAppConfig } from "~/app/config/useAppConfig";
import { Permission, userHasAnyPermission, userHasPermission } from "~/domains/user/utils/permissions";
import { userIsCustomer, userIsPublic } from "~/domains/user/utils/roles";
import { URL_CONTACT_US } from "~/utils/url/staticUrls";
import type { ConfiguratorCommerceRuntime } from "../../application/useConfiguratorCommerceRuntime";
import type { ReadyConfiguratorViewModel } from "../../application/useConfiguratorViewModel";
import { useLinearAvailabilityQuery, useLinearPriceQuery } from "../../data/linearPnAQueries";
import type { ConfiguratorSummaryViewModel } from "../preview/buildConfiguratorPreviewViewModel";
import {
  buildSummaryCapabilities,
  type ConfiguratorSummaryActionId,
  type SummaryAudienceCapabilities,
} from "./summaryCapabilities";
import {
  buildSummaryAvailabilityViewModel,
  buildSummaryPriceViewModel,
  type SummaryActionViewModel,
  type SummaryCommerceViewModel,
} from "./summaryCommerceViewModel";
import {
  useConfiguratorSummaryInteractions,
  type ConfiguratorCartSuccessRenderer,
} from "./useConfiguratorSummaryInteractions";
import { configuratorV2SummaryAnalytics } from "./configuratorV2SummaryAnalytics";

export interface ConfiguratorSummaryController {
  viewModel: ConfiguratorSummaryViewModel & { commerce: SummaryCommerceViewModel };
  setQuantity(quantity: number): void;
  setPriceReference(priceReference: string): void;
  triggerAction(action: ConfiguratorSummaryActionId): void;
}

interface ConfiguratorSummaryControllerInput {
  configurator: ReadyConfiguratorViewModel;
  preview: ConfiguratorSummaryViewModel;
  commerce: ConfiguratorCommerceRuntime;
  isUpdating: boolean;
  renderCartSuccess: ConfiguratorCartSuccessRenderer;
  onRegistrationRequested: () => void;
}

const readAudienceCapabilities = (): SummaryAudienceCapabilities => ({
  audience: userIsPublic() ? "public" : userIsCustomer() ? "customer" : "registered",
  canAdd: userHasAnyPermission(Permission.ADD_TO_CART, Permission.ADD_TO_QUOTE),
  canPurchase: userHasPermission(Permission.LINEAR_PURCHASE),
  canSeePrice: userHasPermission(Permission.PRICE),
  canSeeAvailability: userHasPermission(Permission.AVAILABILITY),
});

const actionPresentation = (
  action: ConfiguratorSummaryActionId,
  audience: SummaryAudienceCapabilities["audience"],
): Pick<SummaryActionViewModel, "color" | "variant"> => {
  if (audience === "public") {
    switch (action) {
      case "requestQuotation":
      case "requestOffer":
        return { color: "primary", variant: "contained" };
      case "saveConfiguration":
        return { color: "secondary", variant: "contained" };
      case "downloadDatasheet":
        return { color: "primary", variant: "text" };
    }
  }
  return {
    color: "primary",
    variant: action === "addToCart" || action === "addToQuote" ? "contained" : "outlined",
  };
};

export const useConfiguratorSummaryController = ({
  configurator,
  preview,
  commerce,
  isUpdating,
  renderCartSuccess,
  onRegistrationRequested,
}: ConfiguratorSummaryControllerInput) => {
  const configuration = configurator.configuration;
  const context = configurator.context;
  const audience = readAudienceCapabilities();
  const webservicesDisabled = useAppConfig()?.configuration.disableAllWS ?? false;
  const configurationComplete = configurator.complete && configuration.conflicts.length === 0;
  const capabilities = buildSummaryCapabilities({
    customization: context.customization,
    audience,
    configurationComplete,
    commerceSettled: !commerce.isSettling,
    commerceSubmitted:
      commerce.submission?.configId === configuration.configId &&
      commerce.submission.quantity === commerce.quantity &&
      commerce.submission.priceReference === commerce.priceReference,
    configurationMutationPending: isUpdating,
    webservicesDisabled,
  });
  const pnaEnabled = configurationComplete && !isUpdating && !commerce.isSettling;
  const priceQuery = useLinearPriceQuery({
    configuration,
    quantity: commerce.settledQuantity,
    priceReference: commerce.priceReference,
    enabled: pnaEnabled && capabilities.showPrice,
  });
  const availabilityQuery = useLinearAvailabilityQuery({
    configuration,
    quantity: commerce.settledQuantity,
    priceReference: commerce.priceReference,
    enabled: pnaEnabled && capabilities.showAvailability,
  });
  const priceViewModel = buildSummaryPriceViewModel(capabilities.showPrice, priceQuery, commerce.settledQuantity);
  const availabilityViewModel = buildSummaryAvailabilityViewModel(capabilities.showAvailability, availabilityQuery);
  const actionEnabled = (action: ConfiguratorSummaryActionId) =>
    capabilities.actions.find((candidate) => candidate.id === action)?.enabled ?? false;
  const interactions = useConfiguratorSummaryInteractions({
    configuration,
    context,
    audience,
    commerce,
    preview,
    priceText:
      priceViewModel.state === "value"
        ? priceViewModel.text
        : priceViewModel.state === "onRequest"
        ? "Price on request"
        : "",
    actionEnabled,
    renderCartSuccess,
    onRegistrationRequested,
  });

  const viewModel = useMemo(
    () => ({
      ...preview,
      commerce: {
        quantity: commerce.quantity,
        showQuantity: capabilities.showQuantity,
        quantitySettling: commerce.isSettling,
        priceReference: commerce.priceReference,
        priceReferences: commerce.priceReferences,
        showPriceReference: capabilities.showPriceReference,
        priceReferenceEditable: capabilities.priceReferenceEditable && commerce.priceReferenceEditable,
        price: priceViewModel,
        availability: availabilityViewModel,
        actions: capabilities.actions.map((action) => ({
          ...action,
          ...actionPresentation(action.id, audience.audience),
          enabled: action.enabled && interactions.directPending === null,
        })),
        contactSpecialistUrl: context.customization.specialistLinkURL || URL_CONTACT_US,
        showCad: capabilities.showCad,
        actionError: interactions.directError ?? undefined,
      },
    }),
    [
      audience.audience,
      availabilityViewModel,
      capabilities,
      commerce.isSettling,
      commerce.priceReference,
      commerce.priceReferenceEditable,
      commerce.priceReferences,
      commerce.quantity,
      context.customization.specialistLinkURL,
      interactions.directError,
      interactions.directPending,
      preview,
      priceViewModel,
    ],
  );

  const trackCadOpened = () =>
    configuratorV2SummaryAnalytics.openedCad({
      productCode: configuration.productCode,
      currentStepNumber: configuration.currentStepNumber,
      designation: configuration.productShortDesignation,
    });

  return {
    controller: {
      viewModel,
      setQuantity: commerce.setQuantity,
      setPriceReference: commerce.setPriceReference,
      triggerAction: interactions.triggerAction,
    } satisfies ConfiguratorSummaryController,
    interactionNode: interactions.interactionNode,
    trackCadOpened,
  };
};
